// AMDiris Iris Logo — purpose-built SVG, replaces generic Eye icon
// Concentric iris rings + cyan/teal gradient + inner pupil + soft glow
import React from "react";

export function AmdirisLogo({ size = 36, className = "", glow = true, showRings = true }) {
  const id = `amd-${Math.random().toString(36).slice(2, 7)}`;
  return (
    <span
      className={`relative inline-flex items-center justify-center ${className}`}
      style={{ width: size, height: size }}
      aria-label="AMDiris"
    >
      {glow && (
        <span
          aria-hidden
          className="absolute inset-0 rounded-full"
          style={{
            background: "radial-gradient(circle, rgba(0,180,216,0.45) 0%, transparent 70%)",
            filter: "blur(6px)",
          }}
        />
      )}
      <svg
        viewBox="0 0 64 64"
        width={size}
        height={size}
        className="relative"
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <radialGradient id={`${id}-iris`} cx="0.5" cy="0.5" r="0.55">
            <stop offset="0%" stopColor="#48CAE4" />
            <stop offset="50%" stopColor="#00B4D8" />
            <stop offset="100%" stopColor="#0D6E8C" />
          </radialGradient>
          <radialGradient id={`${id}-pupil`} cx="0.5" cy="0.5" r="0.5">
            <stop offset="0%" stopColor="#0A1628" />
            <stop offset="100%" stopColor="#020610" />
          </radialGradient>
          <linearGradient id={`${id}-ring`} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#14B8A6" />
            <stop offset="100%" stopColor="#00B4D8" />
          </linearGradient>
        </defs>

        {/* Outer ring */}
        <circle cx="32" cy="32" r="30" fill="none" stroke={`url(#${id}-ring)`} strokeWidth="2" opacity="0.85" />

        {/* Iris body */}
        <circle cx="32" cy="32" r="22" fill={`url(#${id}-iris)`} />

        {/* Iris striations (radial lines) */}
        {showRings && (
          <g stroke="#0A1628" strokeWidth="0.6" opacity="0.55">
            {Array.from({ length: 12 }).map((_, i) => {
              const a = (i * Math.PI) / 6;
              const x1 = 32 + Math.cos(a) * 8;
              const y1 = 32 + Math.sin(a) * 8;
              const x2 = 32 + Math.cos(a) * 21;
              const y2 = 32 + Math.sin(a) * 21;
              return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} />;
            })}
          </g>
        )}

        {/* Inner band */}
        <circle cx="32" cy="32" r="14" fill="none" stroke="#48CAE4" strokeWidth="1" opacity="0.7" />

        {/* Pupil */}
        <circle cx="32" cy="32" r="9" fill={`url(#${id}-pupil)`} />

        {/* Catchlight (highlight) */}
        <circle cx="29" cy="29" r="2.4" fill="#FFFFFF" opacity="0.92" />
        <circle cx="34" cy="34" r="0.9" fill="#FFFFFF" opacity="0.5" />
      </svg>
    </span>
  );
}

export function AmdirisWordmark({ size = 36, className = "" }) {
  return (
    <span className={`inline-flex items-center gap-3 ${className}`}>
      <AmdirisLogo size={size} />
      <span className="flex flex-col leading-none">
        <span
          className="font-bold text-white text-lg tracking-tight"
          style={{ fontFamily: "Outfit, sans-serif", letterSpacing: "0.02em" }}
        >
          AMDiris
        </span>
        <span className="text-[9px] uppercase tracking-[0.25em] text-cyan-300/70 mt-0.5">
          EDC Platform
        </span>
      </span>
    </span>
  );
}

export default AmdirisLogo;
